import { AgGridAngular } from 'ag-grid-angular';
import {
    AllCommunityModule,
    ClientSideRowModelModule,
    ColDef,
    GridOptions,
    ModuleRegistry,
} from 'ag-grid-community';
import { ClipboardModule, ContextMenuModule } from 'ag-grid-enterprise';

import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    inject,
    Input,
    OnDestroy,
    OnInit,
} from '@angular/core';
import { PushPipe } from '@ngrx/component';
import {
    LOCALISED_CURRENCY_VALUE_FORMATTER,
    numberColorCellClass,
    numberFieldComparator,
    numberFieldPropertyComparator,
    PERCENTAGE_VALUE_FORMATTER,
    WdxAgGridCurrencyField,
} from '@wdx/shared/components/ag-grid';
import { WdxSpinnerModule } from '@wdx/shared/components/wdx-spinner';
import {
    FriendlyProblemDetails,
    IMeLocale,
    InvestmentDataConfiguration,
    InvestmentDataSectionType,
    TranslationsService,
    WdxAgGridThemeDirective,
    WdxCurrencyOptions,
    WdxNumberService,
} from '@wdx/shared/utils';
import * as Highcharts from 'highcharts';
import { HighchartsChartModule } from 'highcharts-angular';
import * as R from 'ramda';
import { BehaviorSubject } from 'rxjs';
import {
    PortfolioLoadingStates,
    PortfolioNotLoadedComponent,
} from '../portfolio-not-loaded/portfolio-not-loaded.component';
import { PortfolioAgGridColumnMapperService } from '../services/portfolio-ag-grid-column-mapper.service';
import { renderTotal } from './asset-overview.helpers';

export interface AssetsData {
    currency: WdxCurrencyOptions;
    valuations: {
        name?: string | null;
        valuationValue?: number;
        valuationUnrealisedProfitLoss?: number | null;
        percentageOfTotal?: number | null;
    }[];
}

export interface AssetsOverviewRowData {
    name?: string;
    percentageOfTotal?: number;
    valuationUnrealisedProfitLoss?: WdxAgGridCurrencyField;
    valuationValue?: WdxAgGridCurrencyField;
}

ModuleRegistry.registerModules([
    AllCommunityModule,
    ClientSideRowModelModule,
    ClipboardModule,
    ContextMenuModule,
]);

@Component({
    selector: 'wdx-assets-overview',
    imports: [
        HighchartsChartModule,
        AgGridAngular,
        WdxAgGridThemeDirective,
        WdxSpinnerModule,
        PushPipe,
        PortfolioNotLoadedComponent,
    ],
    providers: [PortfolioAgGridColumnMapperService],
    templateUrl: './assets-overview.component.html',
    styleUrls: ['./assets-overview.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: { '[class]': 'hostClasses' },
})
export class AssetsOverviewComponent implements OnDestroy, OnInit {
    private elementRef = inject(ElementRef);
    private changeDetectorRef = inject(ChangeDetectorRef);
    private localeService = inject(IMeLocale);
    private numberService = inject(WdxNumberService);
    private translationService = inject(TranslationsService);
    private agGridColumnMapperService = inject(
        PortfolioAgGridColumnMapperService,
    );

    private observer = new ResizeObserver((entries) => {
        const columnMode = entries?.[0]?.contentBoxSize?.[0]?.inlineSize < 900;
        if (this.columnMode !== columnMode) {
            this.columnMode = columnMode;
            this.changeDetectorRef.markForCheck();
        }
    });
    get hostClasses() {
        return (
            'd-flex align-items-center justify-content-center' +
            (this.columnMode ? ' flex-column' : '')
        );
    }

    @Input() portfolioConfig?: InvestmentDataConfiguration;
    @Input() dataTypeLabel = 'Assets';
    @Input() dataConnectionError?: FriendlyProblemDetails = undefined;
    @Input() allocationLabel = 'PORTOFOLIO_INSIGHTS_ALLOCATION_LABEL';
    @Input() valueLabel = 'PORTOFOLIO_INSIGHTS_VALUE_LABEL';
    @Input() set assetsData(value: AssetsData | null | undefined) {
        if (value === undefined) {
            this.status = 'loading';
            return;
        }
        this.status = 'loaded';

        if (value === null || !value.currency?.currencyCode) {
            this.rowData = [];
            return;
        }

        const totalValue =
            value.valuations
                ?.map((val) => val.valuationValue)
                .reduce((prev, curr) => (prev || 0) + (curr || 0), 0) || 0;

        const locale = this.localeService.getMeLocale();
        const numberService = this.numberService;
        const formattedTotal = this.numberService.formatLocalisedCurrency(
            totalValue,
            {
                ...value.currency,
                locale: locale?.code,
                groupSeparator: locale?.numberFormat?.numberGroupSeparator,
                decimalSeparator: locale?.numberFormat?.numberDecimalSeparator,
            },
        ) as string;

        this.chartOptions = {
            chart: {
                type: 'pie',
                events: {
                    render: renderTotal(
                        formattedTotal,
                        this.translationService.translateTokenisedString(
                            'PORTFOLIO_ASSETS_TOTAL',
                        ),
                    ),
                },
            },
            title: { text: '' },
            plotOptions: {
                pie: {
                    innerSize: '50%',
                    dataLabels: {
                        enabled: true,
                        formatter: function () {
                            return `${numberService.formatNumber(
                                this.percentage,
                                locale?.code,
                                {
                                    minimumFractionDigits: 1,
                                    maximumFractionDigits: 1,
                                },
                            )} %`;
                        },
                    },
                },
            },
            series: [
                {
                    name: this.translateLabel(
                        'PORTOFOLIO_INSIGHTS_ASSET_ALLOCATION',
                    ),
                    data: value.valuations.map(
                        ({ name, percentageOfTotal }) => ({
                            name: name,
                            y: percentageOfTotal,
                        }),
                    ),
                } as any,
            ],
            credits: {
                enabled: false,
            },
            accessibility: {
                enabled: true,
            },
        };
        this.rowData = value?.valuations?.map(
            (val) =>
                ({
                    ...val,
                    valuationUnrealisedProfitLoss: {
                        value: val.valuationUnrealisedProfitLoss,
                        currency: value.currency,
                    },
                    valuationValue: {
                        value: val.valuationValue,
                        currency: value.currency,
                    },
                }) as AssetsOverviewRowData,
        );
        if (!this.rowData.length) {
            this.status = 'noData';
        }

        this.changeDetectorRef.markForCheck();
    }
    public readonly Highcharts = Highcharts;
    public chartOptions?: Highcharts.Options;
    public rowData?: AssetsOverviewRowData[];
    public status: PortfolioLoadingStates = 'loading';

    public gridOptions: GridOptions = {
        domLayout: 'autoHeight',
        defaultColDef: {
            contextMenuItems: ['copy'],
        },
        onGridReady: this.onGridReady.bind(this),
    };

    public colDefs?: ColDef[];
    private allColDefs: ColDef[] = [];

    public columnMode?: boolean;
    private showAssetChartSubject = new BehaviorSubject(false);
    public showAssetChart$ = this.showAssetChartSubject.asObservable();

    public constructor() {
        this.observer.observe(this.elementRef.nativeElement);
    }

    ngOnInit(): void {
        this.allColDefs = this.buildColDefs();
        const colConfig = this.portfolioConfig?.widgets?.find(
            ({ name }) => name === 'PortfolioAssetClasses',
        );

        const primaryColumnsInConfig = colConfig?.columns?.filter(
            ({ sectionType }) =>
                sectionType === InvestmentDataSectionType.Primary,
        );

        if (primaryColumnsInConfig) {
            // TODO: below is intentionally left commented out, we need to do further work before
            // enabling dynamic column configuration for assets overview, with these the groupby does not work
            // we either need to make that configurable as well or remove it
            // this.colDefs = primaryColumnsInConfig.map((col) =>
            //     this.agGridColumnMapperService.createColDef(col),
            // );
            this.colDefs = R.clone(this.allColDefs);
        } else {
            this.colDefs = R.clone(this.allColDefs);
        }
    }

    public ngOnDestroy(): void {
        this.observer?.unobserve(this.elementRef.nativeElement);
    }

    public onGridReady() {
        // we need to delay the initial chart render to ensure it renders last, otherwise sometimes it will not align correctly
        this.showAssetChartSubject.next(true);
    }

    private buildColDefs(): ColDef[] {
        return [
            {
                headerName: this.translateLabel(this.allocationLabel),
                field: 'percentageOfTotal',
                headerClass: 'ag-right-aligned-header',
                cellClass: 'text-end',
                valueFormatter: PERCENTAGE_VALUE_FORMATTER,
                width: 95,
                comparator: numberFieldComparator('percentageOfTotal'),
            },
            {
                headerName: this.translateLabel('PORTOFOLIO_INSIGHTS_CLASS'),
                field: 'name',
                flex: 1,
                minWidth: 150,
            },
            {
                field: 'valuationUnrealisedProfitLoss',
                headerName: this.translateLabel(
                    'PORTOFOLIO_INSIGHTS_UNREALISED_PROFIT_LOSS',
                ),
                headerClass: 'ag-right-aligned-header',
                cellClass: numberColorCellClass(
                    'text-end',
                    'valuationUnrealisedProfitLoss',
                    'value',
                ),
                width: 120,
                comparator: numberFieldPropertyComparator(
                    'valuationUnrealisedProfitLoss',
                    'value',
                ),
                valueFormatter: LOCALISED_CURRENCY_VALUE_FORMATTER(
                    this.numberService,
                    () => this.localeService.getMeLocale(),
                ),
            },
            {
                headerName: this.translateLabel(this.valueLabel),
                headerClass: 'ag-right-aligned-header',
                field: 'valuationValue',
                cellClass: 'text-end',
                width: 120,
                comparator: numberFieldPropertyComparator(
                    'valuationValue',
                    'value',
                ),
                valueFormatter: LOCALISED_CURRENCY_VALUE_FORMATTER(
                    this.numberService,
                    () => this.localeService.getMeLocale(),
                ),
            },
        ];
    }

    private translateLabel(label: string): string {
        return this.translationService.translateTokenisedString(label);
    }
}
